import * as React from 'react';
import './FloatingInput.scss';
import { InputProps, RawInputProps } from '../types';
import { stripInvisibleCharacters } from '../../../helpers/stringHelpers';
// @ts-ignore
import { FormikErrors, FormikTouched } from 'formik/dist/types';

interface isFieldValidProps {
    touched?: FormikTouched<any>;
    errors: FormikErrors<any>;
    fieldName: string;
    showErrorMessageAfterTouch: boolean;
}

/**
 * Check if formik field is valid
 * @param errors
 * @param fieldName
 */
export const isFieldValid = (errors: FormikErrors<any>, fieldName: string): boolean => {
    return !errors.hasOwnProperty(fieldName);
};

/**
 * Check if formik field is touched
 * @param errors
 * @param fieldName
 */
export const isFieldTouched = (touched: FormikTouched<any>, fieldName: string): boolean => {
    return touched.hasOwnProperty(fieldName);
};

/**
 * Get number of chars remaining
 * @param maxChars
 * @param maxLength
 * @param fieldValue
 */
const getCharsRemaining = (maxChars: number, maxLength: number, fieldValue?: string) => {
    const valueLength = fieldValue?.length ?? 0;
    return (maxChars ?? maxLength) - valueLength > 0 ? (maxChars ?? maxLength) - valueLength : 0;
};

/**
 *
 * @param errorMessage
 * @param name
 * @param isValid
 * @param defaultValue
 * @param maxChars
 * @param maxLength
 * @param placeholder
 * @param fieldIsValid
 * @param setInputValueState
 * @param inputValueState
 * @param required
 * @param onFocus
 * @param props
 * @constructor
 */
export const FloatingInputRaw = ({
    errorMessage,
    isValid,
    defaultValue,
    maxChars,
    maxLength,
    placeholder,
    fieldIsValid,
    setInputValueState,
    inputValueState,
    name,
    required,
    onFocus,
    ...props
}: RawInputProps) => {
    return (
        <div className='floatingInput'>
            <div className='floatingInput__wrap'>
                <input
                    id={name}
                    {...props}
                    onFocus={(e) => {
                        onFocus();
                    }}
                    name={name}
                    value={inputValueState}
                    onChange={(e) => {
                        setInputValueState(stripInvisibleCharacters(e.target.value));
                    }}
                    maxLength={maxLength}
                    className={`floatingInput__input ${
                        !fieldIsValid ? 'floatingInput__input--error' : ''
                    }`}
                    required={required}
                />

                <label htmlFor={name} className='floatingInput__label'>
                    {placeholder}
                </label>
                {(maxLength || maxChars) && (
                    <span
                        className={`floatingInput__chars-remaining ${
                            getCharsRemaining(maxChars, maxLength, inputValueState) === 0
                                ? 'floatingInput__chars-remaining--none'
                                : ''
                        } `}
                    >
                        {getCharsRemaining(maxChars, maxLength, inputValueState)}
                    </span>
                )}
            </div>
            {!isValid && <RawErrorMessage errorMessage={errorMessage} />}
        </div>
    );
};

export const FloatingInput = ({
    maxChars,
    maxLength,
    placeholder,
    showErrorMessageAfterTouch = false,
    ...props
}: InputProps) => {
    const _getCharsRemaining = () => {
        return getCharsRemaining(maxChars, maxLength, props.field?.value);
    };

    const fieldIsValid = isFieldValid(props.form.errors, props.field.name);

    return (
        <div className='floatingInput'>
            <div className='floatingInput__wrap'>
                <input
                    id={props.field.name}
                    maxLength={maxLength}
                    {...props.field}
                    {...props}
                    onChange={(e) => {
                        // pasted text can carry invisible zero-width characters
                        // that render as nothing but fail validation
                        e.target.value = stripInvisibleCharacters(e.target.value);
                        props.field.onChange(e);
                    }}
                    className={`floatingInput__input ${
                        !fieldIsValid ? 'floatingInput__input--error' : ''
                    }`}
                    required
                />
                <label htmlFor={props.field.name} className='floatingInput__label'>
                    {placeholder}
                </label>
                {(maxLength || maxChars) && (
                    <span
                        className={`floatingInput__chars-remaining ${
                            _getCharsRemaining() === 0 ? 'floatingInput__chars-remaining--none' : ''
                        } `}
                    >
                        {_getCharsRemaining()}
                    </span>
                )}
            </div>
            <HSErrorMessage
                showErrorMessageAfterTouch={showErrorMessageAfterTouch}
                touched={props.form.touched}
                errors={props.form.errors}
                fieldName={props.field.name}
            />
        </div>
    );
};

export const FloatingTextArea = ({
    placeholder,
    showErrorMessageAfterTouch = false,
    ...props
}: InputProps) => {
    const fieldIsValid = isFieldValid(props.form.errors, props.field.name);

    return (
        <>
            <div className='floatingInput'>
                <div className='floatingInput__wrap'>
                    <textarea
                        rows={10}
                        {...props.field}
                        {...props}
                        required
                        className={`floatingInput__textarea ${
                            !fieldIsValid ? 'floatingInput__textarea--error' : ''
                        }`}
                    />
                    <span className='floatingInput__label'>{placeholder}</span>
                </div>
            </div>
            <HSErrorMessage
                showErrorMessageAfterTouch={showErrorMessageAfterTouch}
                touched={props.form.touched}
                errors={props.form.errors}
                fieldName={props.field.name}
            />
        </>
    );
};

/**
 *
 * @param touched
 * @param errors
 * @param fieldName
 * @param showErrorMessageAfterTouch
 * @constructor
 */
export const HSErrorMessage = ({
    touched,
    errors,
    fieldName,
    showErrorMessageAfterTouch = false,
}: isFieldValidProps): JSX.Element => {
    const fieldIsValid = isFieldValid(errors, fieldName);
    const fieldIsTouched = isFieldTouched(touched, fieldName);

    if (showErrorMessageAfterTouch && (fieldIsValid || !fieldIsTouched)) return null;
    if (!showErrorMessageAfterTouch && fieldIsValid) return null;

    return <RawErrorMessage errorMessage={errors[fieldName]} />;
};

/**
 *
 * @param errorMessage
 * @constructor
 */
export const RawErrorMessage = ({ errorMessage }): JSX.Element => {
    return (
        <div className='floatingInput__error'>
            <div className='floatingInput__error__text'>{errorMessage}</div>
        </div>
    );
};
